import AddIcon from "@mui/icons-material/Add"
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined"
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined"
import EditOutlinedIcon from "@mui/icons-material/EditOutlined"
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined"
import RefreshIcon from "@mui/icons-material/Refresh"
import SendOutlinedIcon from "@mui/icons-material/SendOutlined"
import StarIcon from "@mui/icons-material/Star"
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import { DataGrid } from "@mui/x-data-grid"

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import MainLayout from "../../layouts/MainLayout"

import * as hrPerformanceService from "../../services/hrPerformanceService"


// =========================================================
// CONSTANTS
// =========================================================

const emptyReviewForm = {
  employee_id: "",
  reviewer_employee_id: "",
  review_period_start: "",
  review_period_end: "",
  review_date: "",
  appreciation: "",
  strengths: "",
  improvement_areas: "",
}


const emptyGoalForm = {
  title: "",
  description: "",
  target_value: "",
  achieved_value: "",
  weight: 0,
  score: "",
  notes: "",
}


// =========================================================
// HELPERS
// =========================================================

const normalizeRows = (result) => {
  if (Array.isArray(result)) {
    return result
  }

  if (Array.isArray(result?.items)) {
    return result.items
  }

  if (Array.isArray(result?.data)) {
    return result.data
  }

  return []
}


const extractErrorMessage = (
  error,
  fallback
) => {
  const detail =
    error?.response?.data?.detail

  if (
    typeof detail ===
    "string"
  ) {
    return detail
  }

  if (
    detail &&
    typeof detail ===
      "object" &&
    !Array.isArray(detail)
  ) {
    return (
      detail.message ||
      detail.detail ||
      fallback
    )
  }

  return (
    error?.response?.data
      ?.message ||
    error?.message ||
    fallback
  )
}


const formatDate = (value) => {
  if (!value) {
    return "-"
  }

  const date =
    new Date(
      `${value}T00:00:00`
    )

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return value
  }

  return new Intl.DateTimeFormat(
    "fr-FR"
  ).format(date)
}


const scoreLabel = (score) => {
  const value =
    Number(score)

  if (
    !Number.isFinite(value)
  ) {
    return "-"
  }

  return `${value.toFixed(1)} / 100`
}


const getStatusConfig = (
  status
) => {
  const value =
    String(
      status || ""
    ).toUpperCase()

  const config = {
    DRAFT: {
      label: "Brouillon",
      color: "default",
    },

    SUBMITTED: {
      label: "Soumise",
      color: "warning",
    },

    VALIDATED: {
      label: "Validée",
      color: "success",
    },

    CANCELLED: {
      label: "Annulée",
      color: "error",
    },
  }

  return (
    config[value] || {
      label:
        status || "-",
      color:
        "default",
    }
  )
}


// =========================================================
// KPI CARD
// =========================================================

function KpiCard({
  title,
  value,
  subtitle,
  icon,
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        minHeight: 116,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="flex-start"
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            flexShrink: 0,
            borderRadius: 2,
            bgcolor: "action.hover",
            display: "grid",
            placeItems: "center",
          }}
        >
          {icon}
        </Box>

        <Box
          sx={{
            minWidth: 0,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {title}
          </Typography>

          <Typography
            variant="h4"
            fontWeight={900}
            sx={{
              fontSize: {
                xs: "1.55rem",
                md: "1.9rem",
              },
            }}
          >
            {value}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
          >
            {subtitle}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}


// =========================================================
// PAGE
// =========================================================

export default function PerformancePage() {
  const [
    rows,
    setRows,
  ] = useState([])

  const [
    formData,
    setFormData,
  ] = useState({
    employees: [],
    statuses: [],
  })

  const [
    summary,
    setSummary,
  ] = useState({})

  const [
    loading,
    setLoading,
  ] = useState(false)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    error,
    setError,
  ] = useState("")

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    employeeFilter,
    setEmployeeFilter,
  ] = useState("all")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("all")

  const [
    reviewDialogOpen,
    setReviewDialogOpen,
  ] = useState(false)

  const [
    detailDialogOpen,
    setDetailDialogOpen,
  ] = useState(false)

  const [
    goalDialogOpen,
    setGoalDialogOpen,
  ] = useState(false)

  const [
    editingReview,
    setEditingReview,
  ] = useState(null)

  const [
    selectedReview,
    setSelectedReview,
  ] = useState(null)

  const [
    editingGoal,
    setEditingGoal,
  ] = useState(null)

  const [
    reviewForm,
    setReviewForm,
  ] = useState(
    emptyReviewForm
  )

  const [
    goalForm,
    setGoalForm,
  ] = useState(
    emptyGoalForm
  )

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    message: "",
    severity: "success",
  })


  const [
    automaticPreview,
    setAutomaticPreview,
  ] = useState(null)

  const [
    automaticLoading,
    setAutomaticLoading,
  ] = useState(false)


  // =======================================================
  // LOAD
  // =======================================================

  const loadData =
    useCallback(
      async () => {
        setLoading(true)
        setError("")

        try {
          const [
            reviewsResult,
            formResult,
            summaryResult,
          ] =
            await Promise.all([
              hrPerformanceService
                .getHrPerformanceReviews({
                  search,
                  employee_id:
                    employeeFilter,
                  status:
                    statusFilter,
                }),

              hrPerformanceService
                .getHrPerformanceFormData(),

              hrPerformanceService
                .getHrPerformanceSummary(),
            ])

          setRows(
            normalizeRows(
              reviewsResult
            )
          )

          setFormData({
            employees:
              formResult
                ?.employees ||
              [],

            statuses:
              formResult
                ?.statuses ||
              [],
          })

          setSummary(
            summaryResult ||
              {}
          )
        } catch (err) {
          setError(
            extractErrorMessage(
              err,
              "Impossible de charger les évaluations."
            )
          )
        } finally {
          setLoading(false)
        }
      },
      [
        search,
        employeeFilter,
        statusFilter,
      ]
    )


  useEffect(() => {
    loadData()
  }, [loadData])


  // =======================================================
  // MESSAGE
  // =======================================================

  const showMessage = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    })
  }


  // =======================================================
  // REVIEW DIALOG
  // =======================================================

  const openCreateReview =
    () => {
      setEditingReview(null)

      setReviewForm({
        ...emptyReviewForm,
      })

      setReviewDialogOpen(true)
    }


  const openEditReview =
    async (row) => {
      try {
        const detail =
          await hrPerformanceService
            .getHrPerformanceReviewById(
              row.id
            )

        setEditingReview(detail)

        setReviewForm({
          employee_id:
            detail.employee_id ||
            "",

          reviewer_employee_id:
            detail
              .reviewer_employee_id ||
            "",

          review_period_start:
            detail
              .review_period_start ||
            "",

          review_period_end:
            detail
              .review_period_end ||
            "",

          review_date:
            detail.review_date ||
            "",

          appreciation:
            detail.appreciation ||
            "",

          strengths:
            detail.strengths ||
            "",

          improvement_areas:
            detail
              .improvement_areas ||
            "",
        })

        setReviewDialogOpen(true)
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de charger l'évaluation."
          ),
          "error"
        )
      }
    }


  const loadAutomaticPreview =
    async () => {
      if (
        !reviewForm.employee_id ||
        !reviewForm.review_period_start ||
        !reviewForm.review_period_end
      ) {
        setAutomaticPreview(null)
        return
      }

      setAutomaticLoading(true)

      try {
        const result =
          await hrPerformanceService
            .getHrPerformanceAutomaticPreview({
              employee_id:
                reviewForm.employee_id,
              review_period_start:
                reviewForm.review_period_start,
              review_period_end:
                reviewForm.review_period_end,
            })

        setAutomaticPreview(
          result || null
        )
      } catch {
        setAutomaticPreview(null)
      } finally {
        setAutomaticLoading(false)
      }
    }


  useEffect(() => {
    if (!reviewDialogOpen) {
      return undefined
    }

    const timer =
      window.setTimeout(
        loadAutomaticPreview,
        250
      )

    return () =>
      window.clearTimeout(
        timer
      )
  }, [
    reviewDialogOpen,
    reviewForm.employee_id,
    reviewForm.review_period_start,
    reviewForm.review_period_end,
  ])


  const saveReview =
    async () => {
      if (
        !reviewForm.employee_id
      ) {
        showMessage(
          "Sélectionnez un employé.",
          "warning"
        )

        return
      }

      if (
        !reviewForm
          .review_period_start ||
        !reviewForm
          .review_period_end
      ) {
        showMessage(
          "Renseignez la période d'évaluation.",
          "warning"
        )

        return
      }

      setSaving(true)

      try {
        const payload = {
          employee_id:
            Number(
              reviewForm.employee_id
            ),

          reviewer_employee_id:
            reviewForm
              .reviewer_employee_id
              ? Number(
                  reviewForm
                    .reviewer_employee_id
                )
              : null,

          review_period_start:
            reviewForm
              .review_period_start,

          review_period_end:
            reviewForm
              .review_period_end,

          review_date:
            reviewForm.review_date ||
            null,

          appreciation:
            reviewForm.appreciation ||
            null,

          strengths:
            reviewForm.strengths ||
            null,

          improvement_areas:
            reviewForm
              .improvement_areas ||
            null,
        }

        if (editingReview) {
          await hrPerformanceService
            .updateHrPerformanceReview(
              editingReview.id,
              payload
            )

          showMessage(
            "Évaluation modifiée avec succès."
          )
        } else {
          await hrPerformanceService
            .createHrPerformanceReview({
              ...payload,
              status: "DRAFT",
              goals: [],
              sync_automatic_metrics: true,
            })

          showMessage(
            "Évaluation créée avec succès."
          )
        }

        setReviewDialogOpen(false)

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible d'enregistrer l'évaluation."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  // =======================================================
  // DETAIL
  // =======================================================

  const openReviewDetail =
    async (row) => {
      try {
        const detail =
          await hrPerformanceService
            .getHrPerformanceReviewById(
              row.id
            )

        setSelectedReview(detail)
        setDetailDialogOpen(true)
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de charger le détail."
          ),
          "error"
        )
      }
    }


  const refreshSelectedReview =
    async () => {
      if (
        !selectedReview?.id
      ) {
        return
      }

      const detail =
        await hrPerformanceService
          .getHrPerformanceReviewById(
            selectedReview.id
          )

      setSelectedReview(detail)
    }


  // =======================================================
  // GOAL
  // =======================================================

  const openCreateGoal =
    () => {
      setEditingGoal(null)

      setGoalForm({
        ...emptyGoalForm,
      })

      setGoalDialogOpen(true)
    }


  const openEditGoal =
    (goal) => {
      setEditingGoal(goal)

      setGoalForm({
        title:
          goal.title ||
          "",

        description:
          goal.description ||
          "",

        target_value:
          goal.target_value ||
          "",

        achieved_value:
          goal.achieved_value ||
          "",

        weight:
          Number(
            goal.weight ||
              0
          ),

        score:
          goal.score ??
          "",

        notes:
          goal.notes ||
          "",
      })

      setGoalDialogOpen(true)
    }


  const saveGoal =
    async () => {
      if (
        !selectedReview?.id
      ) {
        return
      }

      if (
        !goalForm
          .title
          .trim()
      ) {
        showMessage(
          "Le titre de l'objectif est obligatoire.",
          "warning"
        )

        return
      }

      setSaving(true)

      try {
        const payload = {
          title:
            goalForm
              .title
              .trim(),

          description:
            goalForm.description ||
            null,

          target_value:
            goalForm.target_value ||
            null,

          achieved_value:
            goalForm.achieved_value ||
            null,

          weight:
            Number(
              goalForm.weight ||
              0
            ),

          score:
            goalForm.score === ""
              ? null
              : Number(
                  goalForm.score
                ),

          notes:
            goalForm.notes ||
            null,
        }

        if (editingGoal) {
          await hrPerformanceService
            .updateHrPerformanceGoal(
              selectedReview.id,
              editingGoal.id,
              payload
            )

          showMessage(
            "Objectif modifié avec succès."
          )
        } else {
          await hrPerformanceService
            .createHrPerformanceGoal(
              selectedReview.id,
              payload
            )

          showMessage(
            "Objectif ajouté avec succès."
          )
        }

        setGoalDialogOpen(false)

        await refreshSelectedReview()
        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible d'enregistrer l'objectif."
          ),
          "error"
        )
      } finally {
        setSaving(false)
      }
    }


  const deleteGoal =
    async (goal) => {
      if (
        !window.confirm(
          `Supprimer l'objectif « ${goal.title} » ?`
        )
      ) {
        return
      }

      try {
        await hrPerformanceService
          .deleteHrPerformanceGoal(
            selectedReview.id,
            goal.id
          )

        showMessage(
          "Objectif supprimé avec succès."
        )

        await refreshSelectedReview()
        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de supprimer l'objectif."
          ),
          "error"
        )
      }
    }


  const syncAutomaticMetrics =
    async (row) => {
      try {
        await hrPerformanceService
          .syncHrPerformanceAutomaticMetrics(
            row.id
          )

        showMessage(
          "Critères automatiques synchronisés."
        )

        if (
          selectedReview?.id ===
          row.id
        ) {
          await refreshSelectedReview()
        }

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de synchroniser les critères automatiques."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // WORKFLOW
  // =======================================================

  const submitReview =
    async (row) => {
      if (
        !window.confirm(
          "Soumettre cette évaluation ? Elle passera à l'étape de validation."
        )
      ) {
        return
      }

      try {
        await hrPerformanceService
          .submitHrPerformanceReview(
            row.id
          )

        showMessage(
          "Évaluation soumise avec succès."
        )

        setDetailDialogOpen(false)

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de soumettre l'évaluation."
          ),
          "error"
        )
      }
    }


  const validateReview =
    async (row) => {
      if (
        !window.confirm(
          "Valider définitivement cette évaluation ?"
        )
      ) {
        return
      }

      try {
        await hrPerformanceService
          .validateHrPerformanceReview(
            row.id
          )

        showMessage(
          "Évaluation validée avec succès."
        )

        setDetailDialogOpen(false)

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de valider l'évaluation."
          ),
          "error"
        )
      }
    }


  const cancelReview =
    async (row) => {
      if (
        !window.confirm(
          "Annuler cette évaluation ?"
        )
      ) {
        return
      }

      try {
        await hrPerformanceService
          .cancelHrPerformanceReview(
            row.id
          )

        showMessage(
          "Évaluation annulée avec succès."
        )

        setDetailDialogOpen(false)

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible d'annuler l'évaluation."
          ),
          "error"
        )
      }
    }


  const deleteReview =
    async (row) => {
      if (
        !window.confirm(
          "Supprimer définitivement cette évaluation ?"
        )
      ) {
        return
      }

      try {
        await hrPerformanceService
          .deleteHrPerformanceReview(
            row.id
          )

        showMessage(
          "Évaluation supprimée avec succès."
        )

        await loadData()
      } catch (err) {
        showMessage(
          extractErrorMessage(
            err,
            "Impossible de supprimer l'évaluation."
          ),
          "error"
        )
      }
    }


  // =======================================================
  // KPI
  // =======================================================

  const averageScore =
    Number(
      summary
        ?.average_score ||
      0
    ).toFixed(1)


  // =======================================================
  // COLUMNS
  // =======================================================

  const columns =
    useMemo(
      () => [
        {
          field: "employee_name",
          headerName: "Employé",
          minWidth: 190,
          flex: 1,

          renderCell:
            (params) => (
              <Box>
                <Typography
                  variant="body2"
                  fontWeight={700}
                >
                  {params
                    .row
                    .employee_name ||
                    "-"}
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {params
                    .row
                    .employee_code ||
                    ""}
                </Typography>
              </Box>
            ),
        },

        {
          field: "period",
          headerName: "Période",
          minWidth: 190,

          renderCell:
            (params) =>
              `${formatDate(
                params
                  .row
                  .review_period_start
              )} → ${formatDate(
                params
                  .row
                  .review_period_end
              )}`,
        },

        {
          field: "reviewer_name",
          headerName: "Évaluateur",
          minWidth: 170,

          renderCell:
            (params) =>
              params.value ||
              "-",
        },

        {
          field: "goals_count",
          headerName: "Objectifs",
          minWidth: 100,
          align: "center",
          headerAlign: "center",
        },

        {
          field: "overall_score",
          headerName: "Score",
          minWidth: 120,
          align: "center",
          headerAlign: "center",

          renderCell:
            (params) =>
              scoreLabel(
                params.value
              ),
        },

        {
          field: "status",
          headerName: "Statut",
          minWidth: 125,

          renderCell:
            (params) => {
              const item =
                getStatusConfig(
                  params.value
                )

              return (
                <Chip
                  size="small"
                  label={item.label}
                  color={item.color}
                />
              )
            },
        },

        {
          field: "actions",
          headerName: "Actions",
          minWidth: 290,
          sortable: false,
          filterable: false,

          renderCell:
            (params) => {
              const row =
                params.row

              const status =
                String(
                  row.status ||
                    ""
                ).toUpperCase()

              return (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Tooltip title="Voir le détail">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() =>
                        openReviewDetail(
                          row
                        )
                      }
                    >
                      <VisibilityOutlinedIcon
                        fontSize="small"
                      />
                    </IconButton>
                  </Tooltip>

                  {status ===
                    "DRAFT" && (
                    <Tooltip title="Recalculer les données automatiques">
                      <IconButton
                        size="small"
                        color="secondary"
                        onClick={() =>
                          syncAutomaticMetrics(
                            row
                          )
                        }
                      >
                        <SyncOutlinedIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  {status ===
                    "DRAFT" && (
                    <>
                      <Tooltip title="Modifier">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            openEditReview(
                              row
                            )
                          }
                        >
                          <EditOutlinedIcon
                            fontSize="small"
                          />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Soumettre">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() =>
                            submitReview(
                              row
                            )
                          }
                        >
                          <SendOutlinedIcon
                            fontSize="small"
                          />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}

                  {status ===
                    "SUBMITTED" && (
                    <Tooltip title="Valider">
                      <IconButton
                        size="small"
                        color="success"
                        onClick={() =>
                          validateReview(
                            row
                          )
                        }
                      >
                        <FactCheckOutlinedIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  {[
                    "DRAFT",
                    "SUBMITTED",
                  ].includes(
                    status
                  ) && (
                    <Tooltip title="Annuler">
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() =>
                          cancelReview(
                            row
                          )
                        }
                      >
                        <CancelOutlinedIcon
                          fontSize="small"
                        />
                      </IconButton>
                    </Tooltip>
                  )}

                  {[
                    "DRAFT",
                    "CANCELLED",
                  ].includes(
                    status
                  ) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() =>
                        deleteReview(
                          row
                        )
                      }
                      sx={{
                        fontSize: "0.72rem",
                        minWidth: "auto",
                        px: 1,
                      }}
                    >
                      Supprimer
                    </Button>
                  )}
                </Stack>
              )
            },
        },
      ],
      []
    )


  // =======================================================
  // RENDER
  // =======================================================

  return (
    <MainLayout>
      <Box
        sx={{
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },

          minWidth: 0,
        }}
      >
        {/* ================================================= */}
        {/* HEADER */}
        {/* ================================================= */}

        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          spacing={2}
          mb={1}
        >
          <Box>
            <Typography
              variant="h4"
              fontWeight={900}
              sx={{
                fontSize: {
                  xs: "1.7rem",
                  md: "2.125rem",
                },
              }}
            >
              Évaluation & performance
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Objectifs, appréciations, scores, validation et suivi de la performance du personnel.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshIcon />
              }
              onClick={loadData}
              disabled={loading}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddIcon />
              }
              onClick={
                openCreateReview
              }
            >
              Nouvelle évaluation
            </Button>
          </Stack>
        </Stack>


        {/* ================================================= */}
        {/* INFO */}
        {/* ================================================= */}

        <Alert
          severity="info"
          sx={{
            mb: 2,
            borderRadius: 2,
          }}
        >
          Workflow : création en brouillon → définition et notation des objectifs → soumission → validation finale.
          Le score global est calculé automatiquement à partir des objectifs pondérés.
        </Alert>


        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 2,
            }}
          >
            {error}
          </Alert>
        )}


        {/* ================================================= */}
        {/* KPI */}
        {/* ================================================= */}

        <Box
          sx={{
            display: "grid",

            gridTemplateColumns: {
              xs: "1fr",

              sm:
                "repeat(2, minmax(0, 1fr))",

              lg:
                "repeat(4, minmax(0, 1fr))",
            },

            gap: 2,
            mb: 2,
          }}
        >
          <KpiCard
            title="Évaluations"
            value={
              summary
                ?.total_reviews ||
              0
            }
            subtitle="Total enregistré"
            icon={
              <AssessmentOutlinedIcon />
            }
          />

          <KpiCard
            title="À valider"
            value={
              summary
                ?.submitted_reviews ||
              0
            }
            subtitle="Évaluations soumises"
            icon={
              <FactCheckOutlinedIcon />
            }
          />

          <KpiCard
            title="Validées"
            value={
              summary
                ?.validated_reviews ||
              0
            }
            subtitle="Évaluations finalisées"
            icon={
              <FactCheckOutlinedIcon />
            }
          />

          <KpiCard
            title="Score moyen"
            value={
              `${averageScore} / 100`
            }
            subtitle="Moyenne des scores globaux"
            icon={
              <StarIcon />
            }
          />
        </Box>


        {/* ================================================= */}
        {/* FILTERS */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <Box
            sx={{
              display: "grid",

              gridTemplateColumns: {
                xs: "1fr",
                md: "2fr 1fr 1fr",
              },

              gap: 1.5,
            }}
          >
            <TextField
              size="small"
              label="Rechercher"
              placeholder="Employé, matricule, appréciation..."
              value={search}
              onChange={(
                event
              ) =>
                setSearch(
                  event
                    .target
                    .value
                )
              }
            />

            <FormControl size="small">
              <InputLabel>
                Employé
              </InputLabel>

              <Select
                label="Employé"
                value={
                  employeeFilter
                }
                onChange={(
                  event
                ) =>
                  setEmployeeFilter(
                    event
                      .target
                      .value
                  )
                }
              >
                <MenuItem value="all">
                  Tous les employés
                </MenuItem>

                {formData
                  .employees
                  .map(
                    (employee) => (
                      <MenuItem
                        key={
                          employee.id
                        }
                        value={
                          employee.id
                        }
                      >
                        {employee
                          .employee_code
                          ? `${employee.employee_code} — `
                          : ""}
                        {employee.employee_name}
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>

            <FormControl size="small">
              <InputLabel>
                Statut
              </InputLabel>

              <Select
                label="Statut"
                value={
                  statusFilter
                }
                onChange={(
                  event
                ) =>
                  setStatusFilter(
                    event
                      .target
                      .value
                  )
                }
              >
                <MenuItem value="all">
                  Tous les statuts
                </MenuItem>

                {formData
                  .statuses
                  .map(
                    (item) => (
                      <MenuItem
                        key={
                          item.value
                        }
                        value={
                          item.value
                        }
                      >
                        {item.label}
                      </MenuItem>
                    )
                  )}
              </Select>
            </FormControl>
          </Box>
        </Paper>


        {/* ================================================= */}
        {/* TABLE */}
        {/* ================================================= */}

        <Paper
          elevation={0}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 3,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom:
                "1px solid",
              borderColor:
                "divider",
            }}
          >
            <Typography
              variant="h6"
              fontWeight={800}
            >
              Registre des évaluations
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Suivi des évaluations, objectifs, scores et validations du personnel.
            </Typography>
          </Box>

          <Box
            sx={{
              width: "100%",

              height: {
                xs: 470,
                md: 540,
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              rowHeight={58}
              pageSizeOptions={[
                5,
                10,
                25,
              ]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: 0,
                    pageSize: 10,
                  },
                },
              }}
              sx={{
                border: 0,

                "& .MuiDataGrid-columnHeaders":
                  {
                    bgcolor:
                      "action.hover",
                  },

                "& .MuiDataGrid-cell":
                  {
                    display: "flex",
                    alignItems:
                      "center",
                  },
              }}
            />
          </Box>
        </Paper>


        {/* ================================================= */}
        {/* CREATE / EDIT REVIEW */}
        {/* ================================================= */}

        <Dialog
          open={reviewDialogOpen}
          onClose={() =>
            !saving &&
            setReviewDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>
            {editingReview
              ? "Modifier l'évaluation"
              : "Nouvelle évaluation"}
          </DialogTitle>

          <DialogContent dividers>
            <Alert
              severity="info"
              sx={{
                mb: 2,
              }}
            >
              L'évaluation sera enregistrée en brouillon. Les objectifs pourront ensuite être ajoutés et notés depuis le détail.
            </Alert>

            <Box
              sx={{
                display: "grid",

                gridTemplateColumns: {
                  xs: "1fr",

                  md:
                    "repeat(2, minmax(0, 1fr))",
                },

                gap: 2,
              }}
            >
              <FormControl fullWidth>
                <InputLabel>
                  Employé *
                </InputLabel>

                <Select
                  label="Employé *"
                  value={
                    reviewForm
                      .employee_id
                  }
                  onChange={(
                    event
                  ) =>
                    setReviewForm(
                      (current) => ({
                        ...current,

                        employee_id:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                >
                  {formData
                    .employees
                    .map(
                      (employee) => (
                        <MenuItem
                          key={
                            employee.id
                          }
                          value={
                            employee.id
                          }
                        >
                          {employee
                            .employee_code
                            ? `${employee.employee_code} — `
                            : ""}
                          {employee.employee_name}
                        </MenuItem>
                      )
                    )}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>
                  Évaluateur
                </InputLabel>

                <Select
                  label="Évaluateur"
                  value={
                    reviewForm
                      .reviewer_employee_id
                  }
                  onChange={(
                    event
                  ) =>
                    setReviewForm(
                      (current) => ({
                        ...current,

                        reviewer_employee_id:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                >
                  <MenuItem value="">
                    Non défini
                  </MenuItem>

                  {formData
                    .employees
                    .map(
                      (employee) => (
                        <MenuItem
                          key={
                            employee.id
                          }
                          value={
                            employee.id
                          }
                        >
                          {employee
                            .employee_code
                            ? `${employee.employee_code} — `
                            : ""}
                          {employee.employee_name}
                        </MenuItem>
                      )
                    )}
                </Select>
              </FormControl>

              <TextField
                label="Début de période *"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  reviewForm
                    .review_period_start
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      review_period_start:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <TextField
                label="Fin de période *"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  reviewForm
                    .review_period_end
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      review_period_end:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <TextField
                label="Date d'évaluation"
                type="date"
                InputLabelProps={{
                  shrink: true,
                }}
                value={
                  reviewForm
                    .review_date
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      review_date:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <Box />

              <Paper
                elevation={0}
                sx={{
                  gridColumn: {
                    md: "1 / -1",
                  },
                  p: 2,
                  border:
                    "1px solid",
                  borderColor:
                    "divider",
                  borderRadius: 2,
                  bgcolor:
                    "background.default",
                }}
              >
                <Stack
                  direction={{
                    xs: "column",
                    sm: "row",
                  }}
                  justifyContent="space-between"
                  spacing={1}
                  mb={1.5}
                >
                  <Box>
                    <Typography
                      fontWeight={800}
                    >
                      Données automatiques
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      Présences, retards, congés et activité commerciale détectés pour la période.
                    </Typography>
                  </Box>

                  {automaticLoading && (
                    <CircularProgress
                      size={20}
                    />
                  )}
                </Stack>

                {automaticPreview
                  ?.metrics
                  ?.length ? (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm:
                          "repeat(2, minmax(0, 1fr))",
                        lg:
                          "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {automaticPreview
                      .metrics
                      .map(
                        (metric) => (
                          <Paper
                            key={
                              metric.key
                            }
                            elevation={0}
                            sx={{
                              p: 1.5,
                              border:
                                "1px solid",
                              borderColor:
                                "divider",
                              borderRadius: 2,
                              bgcolor:
                                "background.paper",
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {metric.source}
                            </Typography>

                            <Typography
                              fontWeight={800}
                            >
                              {metric.label}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {metric.details ||
                                "Aucune donnée."}
                            </Typography>

                            {metric.score != null && (
                              <Chip
                                size="small"
                                sx={{
                                  mt: 1,
                                }}
                                label={`${Number(
                                  metric.score
                                ).toFixed(
                                  1
                                )} / 100`}
                                color={
                                  Number(
                                    metric.score
                                  ) >=
                                  70
                                    ? "success"
                                    : Number(
                                        metric.score
                                      ) >=
                                      50
                                    ? "warning"
                                    : "error"
                                }
                              />
                            )}
                          </Paper>
                        )
                      )}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Sélectionnez un employé et une période pour calculer les indicateurs.
                  </Typography>
                )}
              </Paper>

              <TextField
                label="Appréciation générale"
                multiline
                minRows={3}
                value={
                  reviewForm
                    .appreciation
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      appreciation:
                        event
                          .target
                          .value,
                    })
                  )
                }
                sx={{
                  gridColumn: {
                    md: "1 / -1",
                  },
                }}
              />

              <TextField
                label="Points forts"
                multiline
                minRows={3}
                value={
                  reviewForm
                    .strengths
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      strengths:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <TextField
                label="Points à améliorer"
                multiline
                minRows={3}
                value={
                  reviewForm
                    .improvement_areas
                }
                onChange={(
                  event
                ) =>
                  setReviewForm(
                    (current) => ({
                      ...current,

                      improvement_areas:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
            }}
          >
            <Button
              onClick={() =>
                setReviewDialogOpen(
                  false
                )
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={saveReview}
              disabled={saving}
            >
              {saving
                ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                )
                : editingReview
                ? "Enregistrer"
                : "Créer l'évaluation"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* ================================================= */}
        {/* DETAIL */}
        {/* ================================================= */}

        <Dialog
          open={detailDialogOpen}
          onClose={() =>
            setDetailDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="lg"
        >
          <DialogTitle>
            Détail de l'évaluation
          </DialogTitle>

          <DialogContent dividers>
            {selectedReview && (
              <Stack spacing={2}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: "1px solid",
                    borderColor:
                      "divider",
                    borderRadius: 3,
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      md: "row",
                    }}
                    justifyContent="space-between"
                    spacing={2}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                      >
                        {selectedReview
                          .employee_name}
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        {selectedReview
                          .employee_code}

                        {selectedReview
                          .department_name
                          ? ` • ${selectedReview.department_name}`
                          : ""}

                        {selectedReview
                          .position_name
                          ? ` • ${selectedReview.position_name}`
                          : ""}
                      </Typography>
                    </Box>

                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                    >
                      <Chip
                        label={
                          getStatusConfig(
                            selectedReview
                              .status
                          ).label
                        }
                        color={
                          getStatusConfig(
                            selectedReview
                              .status
                          ).color
                        }
                      />

                      <Typography
                        variant="h6"
                        fontWeight={900}
                      >
                        {scoreLabel(
                          selectedReview
                            .overall_score
                        )}
                      </Typography>
                    </Stack>
                  </Stack>

                  <Divider
                    sx={{
                      my: 2,
                    }}
                  />

                  <Box
                    sx={{
                      display: "grid",

                      gridTemplateColumns: {
                        xs: "1fr",

                        md:
                          "repeat(3, minmax(0, 1fr))",
                      },

                      gap: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Période
                      </Typography>

                      <Typography>
                        {formatDate(
                          selectedReview
                            .review_period_start
                        )}
                        {" → "}
                        {formatDate(
                          selectedReview
                            .review_period_end
                        )}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Évaluateur
                      </Typography>

                      <Typography>
                        {selectedReview
                          .reviewer_name ||
                          "Non défini"}
                      </Typography>
                    </Box>

                    <Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Date d'évaluation
                      </Typography>

                      <Typography>
                        {formatDate(
                          selectedReview
                            .review_date
                        )}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>


                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border:
                      "1px solid",
                    borderColor:
                      "divider",
                    borderRadius: 3,
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    justifyContent="space-between"
                    spacing={1}
                    mb={1.5}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                      >
                        Indicateurs automatiques
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Calculés depuis Présences, Congés & permissions et Ventes quand les données sont disponibles.
                      </Typography>
                    </Box>

                    {selectedReview
                      .status ===
                      "DRAFT" && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={
                          <SyncOutlinedIcon />
                        }
                        onClick={() =>
                          syncAutomaticMetrics(
                            selectedReview
                          )
                        }
                      >
                        Recalculer
                      </Button>
                    )}
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm:
                          "repeat(2, minmax(0, 1fr))",
                        lg:
                          "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {selectedReview
                      ?.automatic_metrics
                      ?.metrics
                      ?.map(
                        (metric) => (
                          <Paper
                            key={
                              metric.key
                            }
                            elevation={0}
                            sx={{
                              p: 1.5,
                              border:
                                "1px solid",
                              borderColor:
                                "divider",
                              borderRadius: 2,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {metric.source}
                            </Typography>

                            <Typography
                              fontWeight={800}
                            >
                              {metric.label}
                            </Typography>

                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                              }}
                            >
                              {metric.details ||
                                "-"}
                            </Typography>

                            {metric.score !=
                              null && (
                              <Chip
                                size="small"
                                sx={{
                                  mt: 1,
                                }}
                                label={`${Number(
                                  metric.score
                                ).toFixed(
                                  1
                                )} / 100`}
                                color={
                                  Number(
                                    metric.score
                                  ) >=
                                  70
                                    ? "success"
                                    : Number(
                                        metric.score
                                      ) >=
                                      50
                                    ? "warning"
                                    : "error"
                                }
                              />
                            )}
                          </Paper>
                        )
                      )}
                  </Box>
                </Paper>


                {/* ========================================= */}
                {/* OBJECTIFS */}
                {/* ========================================= */}

                <Paper
                  elevation={0}
                  sx={{
                    border:
                      "1px solid",
                    borderColor:
                      "divider",
                    borderRadius: 3,
                    overflow:
                      "hidden",
                  }}
                >
                  <Stack
                    direction={{
                      xs: "column",
                      sm: "row",
                    }}
                    justifyContent="space-between"
                    alignItems={{
                      xs: "stretch",
                      sm: "center",
                    }}
                    spacing={1}
                    sx={{
                      p: 2,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={800}
                      >
                        Objectifs
                      </Typography>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                      >
                        Définition, résultat atteint, pondération et score.
                      </Typography>
                    </Box>

                    {selectedReview
                      .status ===
                      "DRAFT" && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          <AddIcon />
                        }
                        onClick={
                          openCreateGoal
                        }
                      >
                        Ajouter objectif
                      </Button>
                    )}
                  </Stack>

                  <Divider />

                  {selectedReview
                    .goals
                    ?.length ? (
                    <Stack>
                      {selectedReview
                        .goals
                        .map(
                          (goal) => (
                            <Box
                              key={
                                goal.id
                              }
                              sx={{
                                p: 2,
                                borderBottom:
                                  "1px solid",
                                borderColor:
                                  "divider",
                              }}
                            >
                              <Stack
                                direction={{
                                  xs: "column",
                                  md: "row",
                                }}
                                justifyContent="space-between"
                                spacing={2}
                              >
                                <Box
                                  sx={{
                                    flex: 1,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    fontWeight={800}
                                  >
                                    {goal.title}
                                  </Typography>

                                  {goal.description && (
                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        mt: 0.5,
                                      }}
                                    >
                                      {goal.description}
                                    </Typography>
                                  )}

                                  <Stack
                                    direction={{
                                      xs: "column",
                                      sm: "row",
                                    }}
                                    spacing={2}
                                    mt={1}
                                  >
                                    <Typography
                                      variant="body2"
                                    >
                                      Cible :{" "}
                                      <strong>
                                        {goal
                                          .target_value ||
                                          "-"}
                                      </strong>
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                    >
                                      Réalisé :{" "}
                                      <strong>
                                        {goal
                                          .achieved_value ||
                                          "-"}
                                      </strong>
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                    >
                                      Poids :{" "}
                                      <strong>
                                        {Number(
                                          goal.weight ||
                                            0
                                        )}
                                        %
                                      </strong>
                                    </Typography>
                                  </Stack>
                                </Box>

                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Chip
                                    label={
                                      goal.score ==
                                      null
                                        ? "Non noté"
                                        : `${Number(
                                            goal.score
                                          ).toFixed(
                                            1
                                          )} / 100`
                                    }
                                    color={
                                      goal.score ==
                                      null
                                        ? "default"
                                        : Number(
                                            goal.score
                                          ) >=
                                          70
                                        ? "success"
                                        : Number(
                                            goal.score
                                          ) >=
                                          50
                                        ? "warning"
                                        : "error"
                                    }
                                  />

                                  {selectedReview
                                    .status ===
                                    "DRAFT" && (
                                    <>
                                      <Tooltip title="Modifier">
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            openEditGoal(
                                              goal
                                            )
                                          }
                                        >
                                          <EditOutlinedIcon
                                            fontSize="small"
                                          />
                                        </IconButton>
                                      </Tooltip>

                                      <Button
                                        size="small"
                                        color="error"
                                        onClick={() =>
                                          deleteGoal(
                                            goal
                                          )
                                        }
                                        sx={{
                                          minWidth:
                                            "auto",
                                          px: 1,
                                          fontSize:
                                            "0.72rem",
                                        }}
                                      >
                                        Supprimer
                                      </Button>
                                    </>
                                  )}
                                </Stack>
                              </Stack>
                            </Box>
                          )
                        )}
                    </Stack>
                  ) : (
                    <Box
                      sx={{
                        p: 4,
                        textAlign:
                          "center",
                      }}
                    >
                      <Typography
                        color="text.secondary"
                      >
                        Aucun objectif enregistré.
                      </Typography>
                    </Box>
                  )}
                </Paper>


                {/* ========================================= */}
                {/* APPRECIATION */}
                {/* ========================================= */}

                <Box
                  sx={{
                    display: "grid",

                    gridTemplateColumns: {
                      xs: "1fr",

                      md:
                        "repeat(3, minmax(0, 1fr))",
                    },

                    gap: 2,
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border:
                        "1px solid",
                      borderColor:
                        "divider",
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      fontWeight={800}
                      mb={1}
                    >
                      Appréciation
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {selectedReview
                        .appreciation ||
                        "Aucune appréciation."}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border:
                        "1px solid",
                      borderColor:
                        "divider",
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      fontWeight={800}
                      mb={1}
                    >
                      Points forts
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {selectedReview
                        .strengths ||
                        "Non renseigné."}
                    </Typography>
                  </Paper>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border:
                        "1px solid",
                      borderColor:
                        "divider",
                      borderRadius: 3,
                    }}
                  >
                    <Typography
                      fontWeight={800}
                      mb={1}
                    >
                      Points à améliorer
                    </Typography>

                    <Typography
                      variant="body2"
                      color="text.secondary"
                    >
                      {selectedReview
                        .improvement_areas ||
                        "Non renseigné."}
                    </Typography>
                  </Paper>
                </Box>
              </Stack>
            )}
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              onClick={() =>
                setDetailDialogOpen(
                  false
                )
              }
            >
              Fermer
            </Button>

            {selectedReview
              ?.status ===
              "DRAFT" && (
              <Button
                variant="contained"
                startIcon={
                  <SendOutlinedIcon />
                }
                onClick={() =>
                  submitReview(
                    selectedReview
                  )
                }
              >
                Soumettre
              </Button>
            )}

            {selectedReview
              ?.status ===
              "SUBMITTED" && (
              <Button
                variant="contained"
                color="success"
                startIcon={
                  <FactCheckOutlinedIcon />
                }
                onClick={() =>
                  validateReview(
                    selectedReview
                  )
                }
              >
                Valider
              </Button>
            )}
          </DialogActions>
        </Dialog>


        {/* ================================================= */}
        {/* GOAL DIALOG */}
        {/* ================================================= */}

        <Dialog
          open={goalDialogOpen}
          onClose={() =>
            !saving &&
            setGoalDialogOpen(
              false
            )
          }
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle>
            {editingGoal
              ? "Modifier l'objectif"
              : "Nouvel objectif"}
          </DialogTitle>

          <DialogContent dividers>
            <Stack spacing={2}>
              <TextField
                label="Objectif *"
                value={
                  goalForm.title
                }
                onChange={(
                  event
                ) =>
                  setGoalForm(
                    (current) => ({
                      ...current,

                      title:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <TextField
                label="Description"
                multiline
                minRows={2}
                value={
                  goalForm
                    .description
                }
                onChange={(
                  event
                ) =>
                  setGoalForm(
                    (current) => ({
                      ...current,

                      description:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />

              <Box
                sx={{
                  display: "grid",

                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                  },

                  gap: 2,
                }}
              >
                <TextField
                  label="Valeur cible"
                  value={
                    goalForm
                      .target_value
                  }
                  onChange={(
                    event
                  ) =>
                    setGoalForm(
                      (current) => ({
                        ...current,

                        target_value:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                />

                <TextField
                  label="Valeur atteinte"
                  value={
                    goalForm
                      .achieved_value
                  }
                  onChange={(
                    event
                  ) =>
                    setGoalForm(
                      (current) => ({
                        ...current,

                        achieved_value:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                />

                <TextField
                  label="Pondération (%)"
                  type="number"
                  inputProps={{
                    min: 0,
                    max: 100,
                  }}
                  value={
                    goalForm.weight
                  }
                  onChange={(
                    event
                  ) =>
                    setGoalForm(
                      (current) => ({
                        ...current,

                        weight:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                />

                <TextField
                  label="Score / 100"
                  type="number"
                  inputProps={{
                    min: 0,
                    max: 100,
                  }}
                  value={
                    goalForm.score
                  }
                  onChange={(
                    event
                  ) =>
                    setGoalForm(
                      (current) => ({
                        ...current,

                        score:
                          event
                            .target
                            .value,
                      })
                    )
                  }
                />
              </Box>

              <TextField
                label="Notes"
                multiline
                minRows={2}
                value={
                  goalForm.notes
                }
                onChange={(
                  event
                ) =>
                  setGoalForm(
                    (current) => ({
                      ...current,

                      notes:
                        event
                          .target
                          .value,
                    })
                  )
                }
              />
            </Stack>
          </DialogContent>

          <DialogActions
            sx={{
              p: 2,
            }}
          >
            <Button
              onClick={() =>
                setGoalDialogOpen(
                  false
                )
              }
              disabled={saving}
            >
              Annuler
            </Button>

            <Button
              variant="contained"
              onClick={saveGoal}
              disabled={saving}
            >
              {saving
                ? (
                  <CircularProgress
                    size={22}
                    color="inherit"
                  />
                )
                : editingGoal
                ? "Enregistrer"
                : "Ajouter l'objectif"}
            </Button>
          </DialogActions>
        </Dialog>


        {/* ================================================= */}
        {/* SNACKBAR */}
        {/* ================================================= */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          onClose={() =>
            setSnackbar(
              (current) => ({
                ...current,
                open: false,
              })
            )
          }
        >
          <Alert
            severity={
              snackbar.severity
            }
            variant="filled"
            onClose={() =>
              setSnackbar(
                (current) => ({
                  ...current,
                  open: false,
                })
              )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}